// ProfileConfirmation.jsx
// =========================
// PURPOSE:
// This component is shown when a user clicks a password reset link
// AND they have an existing profile in the database.
//
// This handles the edge case where:
// - User signed up but never confirmed their email
// - User forgot their password
// - User wants to update their account
//
// FLOW:
// 1. User clicks "Forgot Password" on login screen
// 2. User receives email with reset link
// 3. User clicks link, arrives here (via PASSWORD_RECOVERY event in App.jsx)
// 4. We show their existing profile data (name, role, salary)
// 5. User can edit their info AND must set a new password
// 6. On submit: update password + update profile + proceed to app
//
// This effectively serves as a "re-confirmation" flow for unconfirmed accounts
// while also being a normal password reset for confirmed accounts.

import React, { useState } from 'react';
import { supabase, updateProfile } from './supabaseClient';

export default function ProfileConfirmation({ user, existingProfile, onComplete, onCancel }) {
  // Pre-fill with existing data
  const [name, setName] = useState(existingProfile?.name || '');
  const [targetRole, setTargetRole] = useState(existingProfile?.targetRole || '');
  const [targetSalaryMin, setTargetSalaryMin] = useState(
    existingProfile?.targetSalaryMin?.toString() || ''
  );
  const [targetSalaryMax, setTargetSalaryMax] = useState(
    existingProfile?.targetSalaryMax?.toString() || ''
  );

  // New password fields
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      // Step 1: Update password in Supabase Auth
      const { error: passwordError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (passwordError) {
        throw passwordError;
      }

      // Step 2: Update profile data in database
      const { error: profileError } = await updateProfile(user.id, {
        name,
        email: user.email,
        target_role: targetRole,
        target_salary_min: parseInt(targetSalaryMin) || 0,
        target_salary_max: parseInt(targetSalaryMax) || 0,
      });

      if (profileError) {
        throw profileError;
      }

      // Success - proceed to main app
      onComplete();
    } catch (err) {
      console.error('Profile confirmation error:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#fffef9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: "'IBM Plex Sans', 'Inter', -apple-system, sans-serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=Playfair+Display:wght@600;700&display=swap');
      `}</style>

      <div
        style={{
          width: '100%',
          maxWidth: '480px',
          background: '#ffffff',
          border: '1px solid #e8e8e8',
          borderRadius: '3px',
          padding: '40px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }}
      >
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <svg
            width="52"
            height="52"
            viewBox="0 0 52 52"
            fill="none"
            style={{ margin: '0 auto 12px' }}
          >
            <path
              d="M8 38 Q16 28, 22 32 T34 26"
              stroke="#3d3d3d"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              opacity="0.8"
            />
            <path
              d="M6 44 Q14 32, 20 38 T32 30"
              stroke="#3d3d3d"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              opacity="0.4"
            />
            <circle cx="8" cy="38" r="2.5" fill="#d4736a" />
            <g transform="rotate(-45 38 14)">
              <rect x="24" y="11" width="20" height="6" fill="#f7c948" stroke="#d4a832" strokeWidth="0.5" />
              <rect x="44" y="11" width="4" height="6" fill="#b8b8b8" />
              <rect x="48" y="11.5" width="3" height="5" rx="0.5" fill="#d4736a" />
              <polygon points="24,11 24,17 20,14" fill="#c9a87c" />
              <polygon points="20,14 22,12.5 22,15.5" fill="#3d3d3d" />
            </g>
          </svg>
          <h1
            style={{
              margin: 0,
              fontSize: '24px',
              fontWeight: '700',
              fontFamily: "'Playfair Display', serif",
              color: '#3d3d3d',
            }}
          >
            Welcome Back
          </h1>
          <p
            style={{
              margin: '8px 0 0',
              fontSize: '13px',
              color: '#7a7a7a',
            }}
          >
            Confirm your details and set a new password
          </p>
        </div>

        {/* Info box explaining what's happening */}
        <div
          style={{
            background: '#f8f6f0',
            border: '1px solid #e8e4d9',
            borderRadius: '3px',
            padding: '12px 16px',
            marginBottom: '24px',
            fontSize: '12px',
            color: '#5a5a5a',
          }}
        >
          <strong style={{ color: '#3d3d3d' }}>We found your account!</strong>
          <br />
          Review your information below and create a new password to continue.
        </div>

        <form onSubmit={handleSubmit}>
          {/* Email (read-only) */}
          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                color: '#7a7a7a',
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: '500',
              }}
            >
              Email
            </label>
            <input
              type="email"
              value={user.email}
              disabled
              style={{
                width: '100%',
                padding: '12px 16px',
                background: '#f5f5f5',
                border: '1px solid #d8d8d8',
                borderRadius: '3px',
                fontSize: '14px',
                color: '#7a7a7a',
                fontFamily: "'IBM Plex Sans', sans-serif",
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Name */}
          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                color: '#7a7a7a',
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: '500',
              }}
            >
              Full Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                background: '#ffffff',
                border: '1px solid #b8b8b8',
                borderRadius: '3px',
                fontSize: '14px',
                color: '#3d3d3d',
                fontFamily: "'IBM Plex Sans', sans-serif",
                boxSizing: 'border-box',
              }}
              placeholder="Jane Smith"
            />
          </div>

          {/* Target Role */}
          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                color: '#7a7a7a',
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: '500',
              }}
            >
              Target Role
            </label>
            <input
              type="text"
              value={targetRole}
              onChange={(e) => setTargetRole(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: '#ffffff',
                border: '1px solid #b8b8b8',
                borderRadius: '3px',
                fontSize: '14px',
                color: '#3d3d3d',
                fontFamily: "'IBM Plex Sans', sans-serif",
                boxSizing: 'border-box',
              }}
              placeholder="e.g., Product Engineer, Sales Manager"
            />
            <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {['Product Engineer', 'Software Engineer', 'Product Manager', 'Sales', 'Operations'].map(
                (role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setTargetRole(role)}
                    style={{
                      background: targetRole === role ? '#f7c948' : '#f5f5f5',
                      border: '1px solid #e5e5e5',
                      borderRadius: '3px',
                      padding: '4px 10px',
                      fontSize: '11px',
                      cursor: 'pointer',
                      color: '#3d3d3d',
                    }}
                  >
                    {role}
                  </button>
                )
              )}
            </div>
          </div>

          {/* Salary Range */}
          <div style={{ marginBottom: '24px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                color: '#7a7a7a',
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: '500',
              }}
            >
              Target Salary Range
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <input
                type="number"
                value={targetSalaryMin}
                onChange={(e) => setTargetSalaryMin(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: '#ffffff',
                  border: '1px solid #b8b8b8',
                  borderRadius: '3px',
                  fontSize: '14px',
                  color: '#3d3d3d',
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  boxSizing: 'border-box',
                }}
                placeholder="Min (e.g., 150000)"
              />
              <input
                type="number"
                value={targetSalaryMax}
                onChange={(e) => setTargetSalaryMax(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: '#ffffff',
                  border: '1px solid #b8b8b8',
                  borderRadius: '3px',
                  fontSize: '14px',
                  color: '#3d3d3d',
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  boxSizing: 'border-box',
                }}
                placeholder="Max (e.g., 200000)"
              />
            </div>
          </div>

          {/* Divider */}
          <div
            style={{
              borderTop: '1px solid #e8e8e8',
              margin: '24px 0',
              position: 'relative',
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: '#fff',
                padding: '0 12px',
                fontSize: '11px',
                color: '#7a7a7a',
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}
            >
              New Password
            </span>
          </div>

          {/* New Password */}
          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                color: '#7a7a7a',
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: '500',
              }}
            >
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: '#ffffff',
                border: '1px solid #b8b8b8',
                borderRadius: '3px',
                fontSize: '14px',
                color: '#3d3d3d',
                fontFamily: "'IBM Plex Sans', sans-serif",
                boxSizing: 'border-box',
              }}
              placeholder="At least 6 characters"
            />
          </div>

          {/* Confirm Password */}
          <div style={{ marginBottom: '24px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                color: '#7a7a7a',
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: '500',
              }}
            >
              Confirm Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: '#ffffff',
                border: '1px solid #b8b8b8',
                borderRadius: '3px',
                fontSize: '14px',
                color: '#3d3d3d',
                fontFamily: "'IBM Plex Sans', sans-serif",
                boxSizing: 'border-box',
              }}
              placeholder="Re-enter your password"
            />
          </div>

          {/* Error message */}
          {error && (
            <div
              style={{
                padding: '12px 16px',
                background: '#fce8e8',
                border: '1px solid #d4736a',
                borderRadius: '3px',
                color: '#943c33',
                fontSize: '13px',
                marginBottom: '16px',
              }}
            >
              {error}
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px 24px',
              background: loading ? '#fdf4d7' : '#f7c948',
              border: '2px solid #d4a832',
              borderRadius: '3px',
              color: loading ? '#9a9a9a' : '#3d3d3d',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: "'IBM Plex Sans', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {loading ? 'Saving...' : 'Confirm & Continue'}
          </button>
        </form>

        {/* Cancel link */}
        <div
          style={{
            marginTop: '24px',
            textAlign: 'center',
            fontSize: '12px',
            color: '#7a7a7a',
          }}
        >
          <button
            onClick={onCancel}
            style={{
              background: 'none',
              border: 'none',
              color: '#8b6914',
              cursor: 'pointer',
              fontWeight: '500',
              padding: 0,
              fontSize: '12px',
            }}
          >
            Cancel and return to login
          </button>
        </div>
      </div>
    </div>
  );
}
